import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from "@angular/core";
import { Observable } from "rxjs/internal/Observable";
import { forkJoin, Subscription, BehaviorSubject } from "rxjs";
import { Dictionary } from "src/app/store/models/common_types";
import {
  computeAverage,
  computeTotal,
  createComparisionCompsTable,
  createComparisionCompsTableRows,
  computeCompanyCount,
  getSelectedMarketItemsList,
  getSelectedMarketIdFromList,
} from "src/app/comps-page/utils";
import {
  CompsTableColumnData,
  CompsTable,
} from "src/app/comps-page/comps-page.model";
import { AppService } from "src/app/app.service";
import { getPeriodTypeDataId } from "src/app/utils/period-utils";
import {
  getTickerIdUrl,
  getTickersChunksForDataCall,
} from "src/app/utils/common";
import { CompsDataService } from "src/app/comps-page/comps-data.service";
import { NzMessageService } from "ng-zorro-antd/message";
import { environment } from "../../../environments/environment";

@Component({
  selector: "app-comparision-grid",
  templateUrl: "./comparision-grid.component.html",
  styleUrls: ["./comparision-grid.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ComparisionGridComponent {
  @Input() isActive;
  @Input() industryId;
  @Input() lineItem;
  @Input() metaReady;
  @Input() currentIndex;
  @Input() period;
  @Input() showBlankRows;
  @Input() tickers;
  @Input() currency;
  @Input() viewBy;
  @Input() revision;
  @Input() periodData;
  @Input() periodLabel;
  @Input() magnitudeMessage;
  @Input() activePeriod;
  @Input() source;
  @Input() highlightBestMatches;
  @Input() fetchAndCreateCompsData;
  @Input() viewportOptions;
  @Input() showCompanyCount;
  @Input() marketItemsData;
  @Input() sortByColumnId;
  @Input() periodUserSetting;
  @Output() OnDataCreationInitiate: EventEmitter<any> = new EventEmitter();
  @Output() OnCompsDataCallInPogressChange: EventEmitter<any> =
    new EventEmitter();

  allTickerInfo: Dictionary<any> = {};
  magnitude;
  unit;
  tickerPeriodEstimationData = {};
  compsTableData: CompsTable = {
    rows: [],
    columns: [],
    columnData: {},
    rowData: {},
    filter: {},
    extras: {},
    title: "",
  };
  lineHasTotal;
  lineItemsData;
  lineTotalMap = {};
  currencySymbol: string;
  assets = environment.assetUrl;
  isAbs: boolean;

  isCompsDataInProgress;
  totalLogicCheckInProgress;

  inViewportChange;

  // subscribers
  compsDataSubscriber: Subscription;
  totalLineItemLogicCheckSubscriber: Subscription;

  //observer
  compsGridData$: BehaviorSubject<any>;

  constructor(
    private ref: ChangeDetectorRef,
    private appService: AppService,
    private compsDataService: CompsDataService,
    private message: NzMessageService,
  ) {
    this.compsGridData$ = new BehaviorSubject<any>(null);
  }

  ngOnChanges(changes) {
    if (
      changes.fetchAndCreateCompsData &&
      changes.fetchAndCreateCompsData.currentValue !==
        changes.fetchAndCreateCompsData.previousValue
    ) {
      this.makeDataCalls();
    }
  }

  makeDataCalls() {
    if (
      this.lineItem === undefined ||
      !this.tickers ||
      this.tickers.length === 0
    ) {
      this.isCompsDataInProgress = true;
      const compsGridData = {
        rows: [],
        columns: [],
        columnData: {},
        rowData: {},
        filter: {},
        extras: {},
        title: "",
      };
      this.isCompsDataInProgress = false;
      this.compsGridData$.next(compsGridData);
      this.ref.detectChanges();
    } else {
      // make data calls
      this.createAndFetchCompTableData();
    }
  }

  createAndFetchCompTableData() {
    if (!this.lineItem) {
      return;
    }

    const marketItems = getSelectedMarketItemsList(this.marketItemsData);

    this.compsTableData = createComparisionCompsTable(
      this.tickers,
      this.lineItem,
      this.magnitudeMessage,
      this.viewBy,
      this.revision,
      this.source,
      this.currency,
      this.period,
      this.periodLabel,
      marketItems,
      true,
      false,
      true,
    );

    const periodType = getPeriodTypeDataId(this.periodData);

    this.compsGridData$.next(this.compsTableData);
    this.ref.detectChanges();

    this.fetchAndHandleCompsData(
      this.viewBy,
      this.revision,
      this.getTickerIdArray(),
      this.period,
      this.lineItem,
      this.source,
      this.currency,
      periodType,
    );
  }

  fetchAndHandleCompsData(
    viewBy: Array<string>,
    revision: string,
    tickers: Array<string>,
    period: string,
    lineItems: Array<any>,
    source: string,
    currency: string,
    periodType: string,
  ) {
    this.isAbs = this.periodUserSetting["type"] === "abs";
    let prevPeriod = "";
    if (viewBy.includes("yoy")) {
      if (period !== "LTM") {
        if (period === "NTM") {
          prevPeriod = "LTM";
        } else if (period === "STM") {
          prevPeriod = "NTM";
        } else {
          if (this.isAbs) {
            const [basis, year] = period.toString().split("-");
            prevPeriod = `${basis}-${parseInt(year) - 1}`;
          } else {
            const basis = period.toString().replace(/[0-9-]/g, "");
            const year = period.toString().replace(/[a-zA-Z]/g, "");
            if (basis.includes("Q")) {
              prevPeriod = basis + (parseInt(year) - 4);
            } else if (basis.includes("H")) {
              prevPeriod = basis + (parseInt(year) - 2);
            } else {
              prevPeriod = basis + (parseInt(year) - 1);
            }
          }
        }
      }
    }
    this.isCompsDataInProgress = true;
    this.OnCompsDataCallInPogressChange.emit(true);

    const dataCalls: Array<Observable<any>> = [];

    let _viewBy = "viewBy=values";
    let _dataExistsCheck = "";
    if (viewBy.includes("values")) {
      _dataExistsCheck += `&data_exists_check=values`;
    }
    if (viewBy.includes("revision")) {
      _viewBy += `&viewBy=revision&revision=${revision}`;
      _dataExistsCheck += `&data_exists_check=revision`;
    } else if (viewBy.includes("yoy")) {
      _viewBy += `&viewBy=yoy`;
      _dataExistsCheck += `&data_exists_check=yoy`;
    }
    let currencyUrl = "";
    if (currency !== "Base") {
      currencyUrl += `&currency=${currency}`;
    }

    const tickersChunks = getTickersChunksForDataCall(tickers, 18);
    const industryParam = this.industryId
      ? `&industry_id=${this.industryId}`
      : "";

    // const periodPayload =
    //   period === "NTM" || period === "LTM" || period === "STM"
    //     ? `&other_period=${period}`
    //     : `&period=${period}&period=${prevPeriod}`;
    let periodPayload = "";
    if (period === "NTM" || period === "LTM" || period === "STM") {
      periodPayload = `&other_period=${period}`;
      if (period !== "LTM" && viewBy.includes("yoy")) {
        periodPayload += `&other_period=${prevPeriod}`;
      }
    } else {
      periodPayload = `&period=${period}`;
      if (viewBy.includes("yoy")) {
        periodPayload += `&period=${prevPeriod}`;
      }
    }

    const marketItems = getSelectedMarketIdFromList(this.marketItemsData);

    const uniquesliids = lineItems.map((item) => item.uniquesliid);
    for (let i = 0; i < tickersChunks.length; i++) {
      const tickerParams = getTickerIdUrl(tickersChunks[i]);
      const uniquesliidsList = `&uniquesliid=${uniquesliids.join(
        "&uniquesliid=",
      )}`;
      let url = `${_viewBy}${_dataExistsCheck}${tickerParams}${industryParam}${currencyUrl}&version=v2`;
      url = `${url}${periodPayload}`;
      url = `${url}&period_type=${periodType}`;
      url = `${url}${uniquesliidsList}&source=${encodeURIComponent(source)}`;
      if (marketItems && marketItems.length) {
        url += `&market_data=${marketItems.join("&market_data=")}`;
      }
      dataCalls.push(this.compsDataService.getCompsData(url));
    }

    if (this.compsDataSubscriber) {
      this.compsDataSubscriber.unsubscribe();
    }
    this.compsDataSubscriber = forkJoin(dataCalls).subscribe((reponseList) => {
      let currencySymbol = "";
      reponseList.forEach((res) => {
        if (res.status === "success") {
          Object.keys(res.data).forEach((ticker) => {
            this.allTickerInfo[ticker] = { ...res.data[ticker] };
            if (this.allTickerInfo[ticker]) {
              lineItems.forEach((item) => {
                if (this.allTickerInfo[ticker][`${item.uniquesliid}|U`]) {
                  item.unit =
                    this.allTickerInfo[ticker][`${item.uniquesliid}|U`];
                }
                if (this.allTickerInfo[ticker][`${item.uniquesliid}|M`]) {
                  item.magnitude =
                    this.allTickerInfo[ticker][`${item.uniquesliid}|M`];
                }
                if (this.allTickerInfo[ticker][`${item.uniquesliid}|C`]) {
                  currencySymbol =
                    this.allTickerInfo[ticker][`${item.uniquesliid}|C`];
                }
              });
            }
          });
        }
      });

      const { rowData } = createComparisionCompsTableRows(
        this.compsTableData.rows,
        this.allTickerInfo,
        this.lineItem,
        period,
        marketItems,
      );

      this.compsTableData.rowData = rowData;

      // this.magnitude = lineInfo.magnitude;
      this.currencySymbol = currencySymbol;

      computeTotal(
        this.compsTableData,
        false,
        period,
        this.currency,
        true,
        this.isAbs,
      );
      computeAverage(this.compsTableData, false, period, this.currency, true);
      computeCompanyCount(this.compsTableData, false, period, true);

      this.compsGridData$.next(this.compsTableData);
      this.ref.detectChanges();

      //update comps table data if remove blanks true , keep it at the end
      // this.updateDataOnShowBlankSwitchChange(this.showBlankRows);
      this.isCompsDataInProgress = false;
      this.OnCompsDataCallInPogressChange.emit(false);
    });
  }

  updateLineInfo(magnitude: string, unit: string) {
    const newColumnData: Dictionary<CompsTableColumnData> = {};

    Object.keys(this.compsTableData.columnData).forEach((key: string) => {
      newColumnData[key] = {
        ...this.compsTableData.columnData[key],
        unit: unit,
        magnitude: magnitude,
      };
    });

    this.compsTableData = {
      ...this.compsTableData,
      columnData: newColumnData,
    };
  }

  getTickerIdArray() {
    return this.compsTableData && this.compsTableData.rows
      ? this.compsTableData.rows.map((item) => item.ticker)
      : [];
  }

  /**
   * It filters the compTableData on the basis of remove blanks switch value
   * If it is true it filters the tickers which have data for all periods
   * @param {boolean} value - elected value of remove blank
   */
  updateDataOnShowBlankSwitchChange(value) {
    this.isCompsDataInProgress = true;
    if (this.compsTableData.rows) {
      if (!value) {
        const currentLineItemId =
          this.lineItem && this.lineItem.uniquesliid
            ? this.lineItem.uniquesliid
            : "";
        const filterTickersWithBlankValues =
          this.compsTableData.rows.filter(
            (item) =>
              this.compsTableData &&
              this.compsTableData.rowData &&
              this.compsTableData.rowData[item.ticker] &&
              this.compsTableData.rowData[item.ticker][
                `${currentLineItemId}|data_exists`
              ],
          ) || [];
        this.compsTableData = {
          ...this.compsTableData,
          rows: filterTickersWithBlankValues,
        };
        this.compsTableData = this.appService.superClone(this.compsTableData);
      } else {
        this.compsTableData = {
          ...this.compsTableData,
          rows: this.getFormattedTickerInfo(this.tickers),
        };
      }

      computeTotal(this.compsTableData, true, "", this.currency, value);
      computeAverage(this.compsTableData, true, "", this.currency, value);

      this.compsTableData = {
        ...this.compsTableData,
      };
    }

    this.isCompsDataInProgress = false;
  }

  getFormattedTickerInfo(tickers) {
    return tickers.map((tickerInfo) => {
      return {
        full_name: tickerInfo.full_name,
        ticker: tickerInfo.ticker,
        sli_comps_id: tickerInfo.companyid,
      };
    });
  }

  toggleFuzzySwitch() {
    if (
      this.highlightBestMatches &&
      this.compsTableData &&
      !this.isCompsDataInProgress
    ) {
      let hasFuzzyData = false;
      const fuzzyCols = this.compsTableData.columns.filter((colKey) => {
        return this.compsTableData.columnData[colKey].isFuzzy;
      });
      if (fuzzyCols.length) {
        hasFuzzyData = true;
      }
      if (!hasFuzzyData) {
        this.message.warning("No best match data for this peer analysis table");
      }
    }
  }
}
